import React, { useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'

import { Button } from '@/components/buttons/Button'
import { openModal, closeModal } from '@/reducers/ui/ui.redux'

import { BasicModal } from './BasicModal'

export const ButtonAndFormModal = ({
  modalId,
  modalTitle,
  modalContent = null,
  modalProps = {},
  buttonLabel,
  buttonProps = {},
  popup = false,
  form,
  icon,
  disabled,
  showButton = true,
  className,
}) => {
  const dispatch = useDispatch()
  const { currentlyOpenModalId } = useSelector((state) => state.ui)
  // Whether the hosted form holds unsaved input, reported through the injected
  // `onDirtyChange`. A ref, not state: AbstractForm reports dirtiness DURING
  // Formik's render (setState there is the update-while-rendering hazard), and
  // the value is only ever read at close-attempt time, so no re-render is
  // needed.
  const dirtyRef = useRef(false)
  const [showCloseWarning, setShowCloseWarning] = useState(false)

  const handleOpen = () => dispatch(openModal(modalId))
  const handleClose = () => {
    dirtyRef.current = false
    setShowCloseWarning(false)
    dispatch(closeModal())
  }
  // Every operator-initiated close path (X, Escape, dimmer, the form's own
  // Close button) funnels here so unsaved input warns before it is lost. The
  // programmatic `modalClose` stays unguarded: forms call it after a save,
  // where Formik still counts the values as dirty but nothing is being lost.
  const attemptClose = () => {
    if (dirtyRef.current) {
      setShowCloseWarning(true)
    } else {
      handleClose()
    }
  }

  return (
    <>
      {showButton && (
        <Button
          data-testid={`${modalId}-button`}
          disabled={disabled}
          onClick={handleOpen}
          className={classNames('no-wrap', className)}
          tooltip={popup ? modalTitle : undefined}
          {...buttonProps}
        >
          {icon}
          {buttonLabel}
        </Button>
      )}

      {currentlyOpenModalId === modalId && (
        <BasicModal
          data-testid={`${modalId}-modal`}
          title={modalTitle}
          onClose={attemptClose}
          show={currentlyOpenModalId === modalId}
          size="small"
          {...modalProps}
        >
          {modalContent}
          {form &&
            React.cloneElement(form, {
              onClose: attemptClose,
              modalClose: handleClose,
              onDirtyChange: (dirty) => {
                dirtyRef.current = dirty
              },
            })}
        </BasicModal>
      )}

      <BasicModal
        title="Unsaved changes"
        show={showCloseWarning}
        onClose={() => setShowCloseWarning(false)}
        size="tiny"
        footer={
          <div className="flex-align-center flex-end small-gap">
            <Button
              basic
              onClick={() => setShowCloseWarning(false)}
              data-testid={`${modalId}-keep-editing-button`}
            >
              Keep editing
            </Button>
            <Button
              critical
              onClick={handleClose}
              data-testid={`${modalId}-discard-changes-button`}
            >
              Discard changes
            </Button>
          </div>
        }
      >
        <p>You have unsaved changes that will be lost.</p>
      </BasicModal>
    </>
  )
}
